<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGtm } from '@gtm-support/vue-gtm'
import { axiosInstance } from '@/api/api'

const { t, locale } = useI18n()
const gtm = useGtm()

const EMAIL_PATTERN = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

const form = reactive({ email: '', terms: false })
const emailError = ref('')
const isSubmitting = ref(false)
const serverError = ref('')
const successMessage = ref('')
const overlayVisible = ref(false)
let overlayTimeout: ReturnType<typeof setTimeout> | null = null

const isValid = computed(() => form.terms && form.email.includes('@') && !emailError.value)

function validateEmail(value: string): boolean {
  if (!value)
    emailError.value = t('Email is required')
  else if (!EMAIL_PATTERN.test(value.trim()))
    emailError.value = t('Email is invalid')
  else
    emailError.value = ''

  return !emailError.value
}

function onBlur() {
  if (form.email.length > 0)
    validateEmail(form.email)
}

function showOverlayFor5s() {
  overlayVisible.value = true
  if (overlayTimeout)
    clearTimeout(overlayTimeout)
  overlayTimeout = setTimeout(() => {
    overlayVisible.value = false
    serverError.value = ''
    successMessage.value = ''
  }, 5000)
}

async function onSubmit() {
  if (!validateEmail(form.email) || !form.terms)
    return

  serverError.value = ''
  successMessage.value = ''
  overlayVisible.value = false
  isSubmitting.value = true

  try {
    await axiosInstance.post('/api/newsletter/subscribe/', { email: form.email.trim() })
    form.email = ''
    form.terms = false
    successMessage.value = t('Thanks! You’re subscribed.')
    gtm?.trackEvent({ event: 'newsletter_form', language: locale.value })
    showOverlayFor5s()
  }
  catch {
    serverError.value = t('We couldn’t subscribe you. Please try again.')
    showOverlayFor5s()
  }
  finally {
    isSubmitting.value = false
  }
}

onBeforeUnmount(() => {
  if (overlayTimeout)
    clearTimeout(overlayTimeout)
})
</script>

<template>
  <form class="newsletter-form" novalidate @submit.prevent="onSubmit">
    <div class="newsletter-input">
      <input
        id="landing-newsletter-email"
        v-model="form.email"
        placeholder="E-mail"
        type="email"
        name="email"
        autocomplete="email"
        @focus="emailError = ''"
        @blur="onBlur"
      >
      <label for="landing-newsletter-email">{{ $t('Your email') }}</label>
      <p v-if="emailError" class="newsletter-input-error">
        {{ emailError }}
      </p>
    </div>
    <label class="newsletter-checkbox">
      <input
        v-model="form.terms" type="checkbox"
        name="terms"
        required
      >
      <span>{{ $t('I consent to subscribe to the e-mail communication from SWIDA Innovative about products and services in the logistics/transport sector.') }}</span>
    </label>
    <button type="submit" class="newsletter-submit" :disabled="!isValid || isSubmitting">
      {{ isSubmitting ? $t('Submitting…') : $t('Subscribe') }}
    </button>
    <Transition name="newsletter-overlay">
      <div v-if="overlayVisible" class="newsletter-overlay">
        <p v-if="serverError" class="newsletter-overlay-error">
          {{ serverError }}
        </p>
        <p v-if="successMessage" class="newsletter-overlay-success">
          {{ successMessage }}
        </p>
      </div>
    </Transition>
  </form>
</template>

<style scoped>
.newsletter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 10px;
  margin: 0;
}

.newsletter-input {
  position: relative;
  width: 100%;
}

.newsletter-input input {
  position: relative;
  display: flex;
  width: 100%;
  margin: 0;
  padding: 16px 20px;
  border: 0;
  border-radius: 12px;
  outline: 1px solid #BFC2D4;
  background-color: #FFFFFF;
  color: #0E0F10;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
}

.newsletter-input input::placeholder {
  color: transparent;
}

.newsletter-input label {
  position: absolute;
  top: 8px;
  left: 12px;
  z-index: 10;
  display: block;
  margin: 0;
  padding: 0 8px;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  background-color: #FFFFFF;
  color: #6E6E6E;
  font-size: 14px;
  line-height: 20px;
  transform: translateY(-16px) scale(0.75);
  transform-origin: left;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}

.newsletter-input input:placeholder-shown + label {
  top: 50%;
  transform: translateY(-50%) scale(1);
}

.newsletter-input input:focus + label {
  top: 8px;
  color: #D81B5D;
  transform: translateY(-16px) scale(0.75);
}

.newsletter-input-error {
  position: absolute;
  top: 100%;
  left: 0;
  margin: 0;
  padding: 1px 20px;
  color: #D81B5D;
  font-size: 10px;
  line-height: 1.5;
}

.newsletter-checkbox {
  display: grid;
  grid-template-columns: 18px auto;
  align-items: start;
  gap: 8px;
  width: 100%;
  margin: 4px 0 0 4px;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.25;
  cursor: pointer;
}

.newsletter-checkbox input {
  display: grid;
  place-content: center;
  width: 16px;
  height: 16px;
  margin: 0;
  border: 1px solid #9B9B9B;
  border-radius: 4px;
  background-color: #FFFFFF;
  appearance: none;
  cursor: pointer;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.newsletter-checkbox input::before {
  width: 0.75em;
  height: 0.75em;
  box-shadow: inset 1em 1em currentColor;
  content: "";
  clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
  transform: scale(0);
  transform-origin: bottom left;
  transition: transform 120ms ease-in-out;
}

.newsletter-checkbox input:checked {
  border-color: #D81B5D;
  background-color: #D81B5D;
  color: #FFFFFF;
}

.newsletter-checkbox input:checked::before {
  transform: scale(1);
}

.newsletter-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-width: 243px;
  margin: 8px 0 0;
  padding: 16px 20px;
  border: 0;
  border-radius: 12px;
  background-color: #D81B5D;
  color: #FFFFFF;
  font: inherit;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  transition: color 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.newsletter-submit:hover {
  background-color: #AE134A;
}

.newsletter-submit[disabled] {
  opacity: 0.5;
  pointer-events: none;
}

.newsletter-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #FFFFFF;
  text-align: center;
}

.newsletter-overlay p {
  width: 100%;
  margin: 0 0 0 4px;
  font-size: 14px;
  line-height: 20px;
}

.newsletter-overlay-error {
  color: #D81B5D;
}

.newsletter-overlay-success {
  color: #008236;
}

.newsletter-overlay-enter-active {
  transition: opacity 200ms ease-out;
}

.newsletter-overlay-leave-active {
  transition: opacity 300ms ease-in;
}

.newsletter-overlay-enter-from,
.newsletter-overlay-leave-to {
  opacity: 0;
}
</style>
